import { FC, useEffect, useState } from "react";
import { Button, Col, Input, Row, message } from "antd";
import { ImportOutlined } from "@ant-design/icons";

import { DealAnalysisOutput } from "../../../../types/ProjectTypes";
import { convertGoogleDoc } from "../../../../common/api/ReportClient";
import { handleError } from "../../../../common/ErrorHandling";
import { AnalysisReport } from "../AnalysisReport";
import { GoogleShareHint } from "./GoogleShareHint";

interface DealReportTabProps {
    projectId: number;
    analysisVersion: number;
    output: DealAnalysisOutput;
    onChange: (output: DealAnalysisOutput) => void;
}

/**
 * Import report from Google doc and saves it on DealAnalysisOutput.
 */
export const DealReportTab: FC<DealReportTabProps> = ({
    projectId,
    analysisVersion,
    output,
    onChange,
}) => {
    const [googleDocUrl, setGoogleDocUrl] = useState("");
    const [isImporting, setIsImporting] = useState(false);

    useEffect(() => {
        setGoogleDocUrl(output.sourceUrls?.report ?? "");
    }, [output]);

    const handleImport = async () => {
        if (!googleDocUrl.trim()) {
            message.warning("Please enter a Google Doc URL or File ID");
            return;
        }
        setIsImporting(true);
        try {
            const result = (await convertGoogleDoc(googleDocUrl.trim())) as {
                data: { sections: Array<{ id: number; title: string; content: string }> };
            };
            const sections = result.data?.sections;
            if (sections && sections.length > 0) {
                onChange({
                    ...output,
                    report: {
                        contractSummary: sections[0]?.content ?? "",
                        negotiationStrategy: sections[1]?.content ?? "",
                        topLegalPriorities: sections[2]?.content ?? "",
                    },
                    sourceUrls: { ...output.sourceUrls, report: googleDocUrl.trim() },
                });
                message.success("Google Doc imported");
            } else {
                message.error("No sections found in the Google Doc");
            }
        } catch (error) {
            handleError(error);
        } finally {
            setIsImporting(false);
        }
    };

    return (
        <div>
            {/* Google Doc import */}
            <Row gutter={8} style={{ marginBottom: 16 }}>
                <Col flex="auto">
                    <Input
                        placeholder="Enter Google Doc URL or File ID"
                        value={googleDocUrl}
                        onChange={(e) => setGoogleDocUrl(e.target.value)}
                        onPressEnter={handleImport}
                        disabled={isImporting}
                    />
                </Col>
                <Col>
                    <Button icon={<ImportOutlined />} onClick={handleImport} loading={isImporting}>
                        Import
                    </Button>
                </Col>
            </Row>
            <GoogleShareHint />

            <AnalysisReport
                projectId={projectId}
                analysisVersion={analysisVersion}
                output={output}
            />
        </div>
    );
};
